import { parseExpressionAt } from "acorn";
import type { StructuredSchema } from "./structured-schema";

const blocked = new Set(["__proto__", "prototype", "constructor", "caller", "callee"]);
const cache = new Map<string, any>();
const object = (value: unknown): Record<string, unknown> => value && typeof value === "object" ? value as Record<string, unknown> : {};
const list = (value: unknown): unknown[] => Array.isArray(value) ? value : [];
function property(value: unknown, key: unknown): any {
  const name = String(key);
  if (blocked.has(name)) throw new Error("Forbidden property");
  if (name === "length" && (typeof value === "string" || Array.isArray(value))) return value.length;
  return value != null && Object.hasOwn(Object(value), name) ? (value as any)[name] : undefined;
}

/** Interpret a pure expression AST. Never execute exported code with eval/Function. */
export function evaluateComputedExpression(source: string, self: Record<string, unknown>, load: (reference: unknown) => unknown = () => undefined): unknown {
  if (source.length > 24000) throw new Error("Expression too large");
  let ast = cache.get(source);
  if (!ast) { ast = parseExpressionAt(source, 0, { ecmaVersion: 2022, preserveParens: true }); if (source.slice(ast.end).trim()) throw new Error("Expected one expression"); if (cache.size > 1000) cache.clear(); cache.set(source, ast); }
  let budget = 20000;
  const functions: Record<string, (...args: any[]) => unknown> = {
    floor: Math.floor, ceil: Math.ceil, round: Math.round, abs: Math.abs, min: Math.min, max: Math.max,
    clamp: (n, low, high) => Math.min(high, Math.max(low, n)),
    number: (value) => Number(value ?? 0), string: (value) => String(value ?? ""),
    sum: (value) => list(value).reduce<number>((sum, item) => sum + Number(item ?? 0), 0),
    count: (value) => typeof value === "string" || Array.isArray(value) ? value.length : Object.keys(object(value)).length,
    entries: (value) => Object.entries(object(value)), keys: (value) => Object.keys(object(value)), values: (value) => Object.values(object(value)),
    at: (value, index) => list(value).at(Number(index)), load,
  };
  const run = (node: any, scope: Record<string, unknown>, depth = 0): any => {
    if (--budget < 0 || depth > 90) throw new Error("Expression budget exceeded");
    const go = (child: any) => run(child, scope, depth + 1);
    if (!node) return undefined;
    switch (node.type) {
      case "Literal": if (node.regex) throw new Error("Regex is not supported"); return node.value;
      case "Identifier": return property(scope, node.name);
      case "ChainExpression": case "ParenthesizedExpression": return go(node.expression);
      case "TemplateLiteral": return node.quasis.map((part: any, index: number) => String(part.value.cooked ?? part.value.raw) + (index < node.expressions.length ? String(go(node.expressions[index])) : "")).join("");
      case "ArrayExpression": return node.elements.map(go);
      case "ObjectExpression": return Object.fromEntries(node.properties.map((entry: any) => {
        if (entry.type !== "Property" || entry.kind !== "init" || entry.method) throw new Error("Only data properties are allowed");
        const key = entry.computed ? go(entry.key) : entry.key.name ?? entry.key.value;
        if (blocked.has(String(key))) throw new Error("Forbidden property");
        return [key, go(entry.value)];
      }));
      case "MemberExpression": return property(go(node.object), node.computed ? go(node.property) : node.property.name);
      case "ConditionalExpression": return go(node.test) ? go(node.consequent) : go(node.alternate);
      case "LogicalExpression": { const left = go(node.left); return node.operator === "??" ? left ?? go(node.right) : node.operator === "&&" ? left && go(node.right) : left || go(node.right); }
      case "UnaryExpression": { const value = go(node.argument); if (node.operator === "!") return !value; if (node.operator === "-") return -Number(value); if (node.operator === "+") return Number(value); throw new Error("Unsupported unary operation"); }
      case "BinaryExpression": {
        const a = go(node.left), b = go(node.right);
        switch (node.operator) {
          case "+": return typeof a === "string" || typeof b === "string" ? String(a) + String(b) : Number(a) + Number(b);
          case "-": return Number(a) - Number(b); case "*": return Number(a) * Number(b); case "/": return Number(a) / Number(b); case "%": return Number(a) % Number(b); case "**": return Number(a) ** Number(b);
          case "==": case "===": return a === b; case "!=": case "!==": return a !== b;
          case ">": return a > b; case "<": return a < b; case ">=": return a >= b; case "<=": return a <= b;
          default: throw new Error("Unsupported binary operation");
        }
      }
      case "ArrowFunctionExpression": {
        if (!node.expression || node.params.some((param: any) => param.type !== "Identifier" || blocked.has(param.name))) throw new Error("Only pure lambdas are allowed");
        return (...args: unknown[]) => run(node.body, { ...scope, ...Object.fromEntries(node.params.map((p: any, i: number) => [p.name, args[i]])) }, depth + 1);
      }
      case "CallExpression": {
        const args = node.arguments.map(go);
        if (node.callee.type === "Identifier" && Object.hasOwn(functions, node.callee.name)) return functions[node.callee.name]!(...args);
        if (node.callee.type !== "MemberExpression") throw new Error("Call is not allowed");
        const target = go(node.callee.object), name = node.callee.computed ? go(node.callee.property) : node.callee.property.name;
        if (Array.isArray(target)) {
          if (target.length > 2000) throw new Error("Collection too large");
          if (["map", "filter", "find", "some", "every"].includes(name) && node.arguments[0]?.type === "ArrowFunctionExpression") return (Array.prototype as any)[name].call(target, args[0]);
          if (["at", "slice", "join", "indexOf", "includes"].includes(name)) return (Array.prototype as any)[name].apply(target, args);
        }
        if (typeof target === "string" && ["toLowerCase", "toUpperCase", "trim", "slice", "indexOf", "includes", "startsWith", "endsWith"].includes(name)) return (String.prototype as any)[name].apply(target, args);
        if (target == null && node.optional) return undefined;
        throw new Error(`Unsupported method: ${String(name)}`);
      }
      default: throw new Error(`Unsupported expression: ${node.type}`);
    }
  };
  const result = run(ast, { $self: self, undefined: undefined });
  if (typeof result === "number" && !Number.isFinite(result)) throw new Error("Non-finite result");
  return result;
}

export function projectComputedFields(values: Record<string, unknown>, schema: StructuredSchema, load?: (reference: unknown) => unknown): { values: Record<string, unknown>; errors: Record<string, string> } {
  const output = structuredClone(values), errors: Record<string, string> = {};
  const pending = new Map(Object.entries(schema.properties ?? {}).filter(([, field]) => field.expression));
  const evaluating = new Set<string>();
  const resolve = (key: string): unknown => {
    const field = pending.get(key); if (!field) return output[key];
    if (evaluating.has(key)) throw new Error(`Computed dependency cycle: ${key}`);
    evaluating.add(key);
    try {
      const self = new Proxy(output, { get: (_, name) => resolve(String(name)), getOwnPropertyDescriptor: (_, name) => ({ configurable: true, enumerable: true, value: output[String(name)] }), ownKeys: () => [...new Set([...Object.keys(output), ...pending.keys()])] });
      output[key] = evaluateComputedExpression(field.expression!, self, load);
      pending.delete(key); delete errors[key]; return output[key];
    } finally { evaluating.delete(key); }
  };
  for (const key of [...pending.keys()]) {
    try { resolve(key); } catch (error) { delete output[key]; errors[key] = error instanceof Error ? error.message : String(error); }
  }
  return { values: output, errors };
}
